import { Check, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { PublicUser } from '@sirocco/shared';
import { events as eventsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState } from '../../components/ui/Bits';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { PersonRow } from './PersonRow';
import { t } from '../../i18n';

/**
 * Les demandes en attente, page a part.
 *
 * Meme forme que les participants, et pour la meme raison : c'est une
 * liste de gens, elle se parcourt et se traite, elle n'a pas a partager
 * l'ecran avec le reste.
 *
 * # Une file, deux raisons d'y etre
 *
 * On attend parce que l'organisateur valide chaque venue, ou parce que
 * toutes les places sont prises. La base ne distingue pas les deux — une
 * demande en attente est une demande en attente —, mais l'ecran le dit,
 * parce que ce n'est pas la meme attente : l'une demande une decision,
 * l'autre un desistement.
 *
 * # Dans l'ordre ou elles sont arrivees
 *
 * La plus ancienne en tete, toujours : c'est la seule facon d'etre juste
 * quand une seule place se libere. Le tri se fait a la lecture, sur
 * l'heure rangee a cote de chaque demande.
 */
export function EventRequestsScreen() {
  const { eventId = '' } = useParams();
  const { user } = useSession();
  const { data, loading, error, reload, setData } = useAsync(
    () => eventsApi.get(eventId),
    [eventId],
    `event:${eventId}`,
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recherche, setRecherche] = useState('');

  /* Sans accent ni majuscule : « chaima » doit trouver « Chaïma.H ». */
  const aplati = (texte: string) =>
    texte
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const isOrganizer = Boolean(data && user && data.organizer.id === user.id);
  const complet = Boolean(data && data.participants.length >= data.maxParticipants);

  /*
    Le rang se calcule avant de filtrer : cherchee ou non, une personne
    garde sa place dans la file. Le recalculer apres ferait dire « 1re »
    a la seule ligne trouvee.
  */
  const vues = (data?.pendingParticipants ?? [])
    .map((member, rang) => ({ member, rang }))
    .filter(({ member }) => aplati(member.username).includes(aplati(recherche.trim())));

  async function accepter(member: PublicUser) {
    if (!data) return;
    setBusy(member.id);
    setNotice(null);
    try {
      setData(await eventsApi.approve(data, member.id));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.carte.ecran.impossible);
    } finally {
      setBusy(null);
    }
  }

  async function refuser(member: PublicUser) {
    if (!data) return;
    setBusy(member.id);
    setNotice(null);
    try {
      setData(await eventsApi.remove(data, member.id));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.carte.ecran.impossible);
    } finally {
      setBusy(null);
    }
  }

  return (
    <SubScreen
      entree="zoom"
      title={t.sorties.demandes.titre}
      action={data && <span className="feed__count">{data.pendingParticipants.length}</span>}
    >
      {loading && !data && <ScreenLoader />}
      {error && !data && <ScreenError message={error} onRetry={reload} />}

      {data && !isOrganizer && (
        <EmptyState
          title={t.sorties.reserve.titre}
          hint={t.sorties.demandes.reserveAide}
        />
      )}

      {data && isOrganizer && (
        <>
          {notice && <p className="field__error">{notice}</p>}

          {/*
            La meme recherche que chez les participants.

            Elle sert moins souvent — une file est plus courte qu'une
            liste d'inscrits — mais quand on cherche quelqu'un de precis
            dans trente demandes, c'est le meme geste, et il doit se
            trouver au meme endroit.
          */}
          {data.pendingParticipants.length > 0 && (
            <label className="ev-panneau__recherche">
              <MagnifyingGlass size={18} />
              <input
                type="search"
                value={recherche}
                placeholder={t.sorties.demandes.chercher}
                aria-label={t.sorties.demandes.chercher}
                onChange={(evenement) => setRecherche(evenement.target.value)}
              />
            </label>
          )}

          {vues.length === 0 && data.pendingParticipants.length > 0 ? (
            <p className="ev-manage__none">{t.sorties.demandes.personne}</p>
          ) : data.pendingParticipants.length === 0 ? (
            <EmptyState
              title={t.sorties.demandes.aucune}
              hint={
                data.requiresValidation
                  ? t.sorties.demandes.aucuneAide
                  : t.sorties.demandes.ouvert
              }
            />
          ) : (
            vues.map(({ member, rang }) => (
              <PersonRow
                key={member.id}
                member={member}
                /* Le rang plutot qu'une phrase : dans une file, ce qu'on
                   veut savoir c'est sa place. */
                note={complet ? t.sorties.demandes.rang(rang + 1) : undefined}
              >
                <button
                  className="ev-person__act ev-person__act--yes"
                  aria-label={t.sorties.demandes.accepter(member.username)}
                  disabled={busy !== null || complet}
                  onClick={() => void accepter(member)}
                >
                  <Check size={20} weight="bold" />
                </button>
                <button
                  className="ev-person__act ev-person__act--no"
                  aria-label={t.sorties.demandes.refuser(member.username)}
                  disabled={busy !== null}
                  onClick={() => void refuser(member)}
                >
                  <X size={20} weight="bold" />
                </button>
              </PersonRow>
            ))
          )}
        </>
      )}
    </SubScreen>
  );
}
